import type { ReactNode } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { highlightVocabulary } from "@/lib/highlightVocabulary";

export type MathPart =
  | { kind: "text"; value: string }
  | { kind: "math"; latex: string; display: boolean };

/**
 * Convert LaTeX-style delimiters from backend JSON into `$` / `$$` so one parser handles all.
 * Display: `\[...\]` → `$$...$$`. Inline: `\(...\)` → `$...$`.
 */
export function normalizeMathDelimiters(input: string): string {
  if (!input) return "";
  let s = input;
  s = s.replace(/\\\[([\s\S]*?)\\\]/g, (_, inner: string) => `$$${inner.trim()}$$`);
  s = s.replace(/\\\(([\s\S]*?)\\\)/g, (_, inner: string) => `$${inner.trim()}$`);
  return s;
}

/** Split `$...$` (inline) and `$$...$$` (display) segments. Unmatched `$` stays text. */
export function splitMathParts(input: string): MathPart[] {
  if (!input) return [];
  const parts: MathPart[] = [];
  const inputNorm = normalizeMathDelimiters(input);
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf) {
      parts.push({ kind: "text", value: buf });
      buf = "";
    }
  };
  while (i < inputNorm.length) {
    if (inputNorm[i] === "$" && inputNorm[i + 1] === "$") {
      flush();
      const end = inputNorm.indexOf("$$", i + 2);
      if (end === -1) {
        buf += inputNorm.slice(i);
        flush();
        break;
      }
      parts.push({ kind: "math", latex: inputNorm.slice(i + 2, end).trim(), display: true });
      i = end + 2;
    } else if (inputNorm[i] === "$") {
      flush();
      const end = inputNorm.indexOf("$", i + 1);
      if (end === -1) {
        buf += inputNorm.slice(i);
        flush();
        break;
      }
      parts.push({ kind: "math", latex: inputNorm.slice(i + 1, end).trim(), display: false });
      i = end + 1;
    } else {
      buf += inputNorm[i];
      i++;
    }
  }
  flush();
  return parts;
}

function renderKatexHtml(latex: string, displayMode: boolean): string {
  return katex.renderToString(latex, {
    throwOnError: false,
    displayMode,
    output: "html",
  });
}

/**
 * Full string → HTML (for legacy `innerHTML` only). Prefer `<MathRichText />` in React.
 * Alias: `renderMath` (matches backend docs).
 */
export function renderMathToHtml(text: string): string {
  const parts = splitMathParts(text);
  return parts
    .map((p) => {
      if (p.kind === "text") return escapeHtml(p.value);
      if (!p.latex) return "";
      return renderKatexHtml(p.latex, p.display);
    })
    .join("");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

interface MathRichTextProps {
  text: string;
  className?: string;
  /** When true, run child-theme vocabulary highlights on text segments only */
  highlightVocab?: boolean;
  isChild?: boolean;
  /** Light text on dark slide — KaTeX inherits readable colour */
  childTheme?: boolean;
}

/**
 * Renders optional KaTeX: `$x+1$` / `\(...\)`, display `$$...$$` / `\[...\]`.
 * Plain English without delimiters stays as text.
 */
const katexChild = "[&_.katex]:!text-white [&_.katex_*]:!text-white [&_.katex_*]:!fill-white";

export function MathRichText({
  text,
  className,
  highlightVocab,
  isChild,
  childTheme,
}: MathRichTextProps) {
  if (!text) return null;
  const parts = splitMathParts(text);
  const nodes: ReactNode[] = [];
  parts.forEach((p, i) => {
    if (p.kind === "text") {
      const inner =
        highlightVocab && isChild !== undefined
          ? highlightVocabulary(p.value, isChild)
          : p.value;
      nodes.push(
        <span key={`t-${i}`} className={className ?? undefined}>
          {inner}
        </span>
      );
    } else {
      if (!p.latex) {
        nodes.push(<span key={`m-${i}`} />);
        return;
      }
      const html = renderKatexHtml(p.latex, p.display);
      const tone = childTheme ? katexChild : "";
      if (p.display) {
        nodes.push(
          <div
            key={`m-${i}`}
            className={`katex-display-wrapper my-2 overflow-x-auto text-center ${tone}`}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      } else {
        nodes.push(
          <span
            key={`m-${i}`}
            className={`inline-block align-middle mx-0.5 ${tone}`}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
    }
  });
  return <>{nodes}</>;
}

/** Same as `renderMathToHtml` — matches backend integration docs. */
export const renderMath = renderMathToHtml;
